import React, { useState, useCallback, useEffect } from "react";
import Select from "react-select";
import { ThreeDots } from 'react-loader-spinner';
import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";
import { toast } from 'react-toastify';

const customStyles = {
    control: (provided) => ({
        ...provided,
        backgroundColor: "#000",
        borderColor: "#374151",
        color: "white",
        padding: "0.1rem 0.25rem",
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "white",
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: "#111",
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#1f2937" : "#111",
        color: "white",
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "white",
    }),
    input: (provided) => ({
        ...provided,
        color: "white",
    }),
};

export default function Settings() {
    const [tab, setTab] = useState("general");
    const [timezoneOptions, setTimezoneOptions] = useState([]);
    const [userInfo, setUserInfo] = useState({
        username: '',
        name: '',
        email: '',
        timezone: '',
        openBalance: '',
    });
    const [selectedTimezone, setSelectedTimezone] = useState(null);
    const [loading, setLoading] = useState(false);
    const [passwords, setPasswords] = useState({ new: "", confirm: "" });

    const fetchUserInfo = useCallback(async () => {
        setLoading(true);
        try {
            const tzRes = await axiosInstance.get(API_ENDPOINTS.TIMEZONE);
            const userRes = await axiosInstance.get(API_ENDPOINTS.PROFILE);

            const options = tzRes.data.data.map(tz => ({
                label: tz.replace(/_/g, " "),
                value: tz,
            }));
            const defaultOption = options.find(opt => opt.value === tzRes.data.default);

            setTimezoneOptions(options);
            setSelectedTimezone(defaultOption || null);
            setUserInfo({
                username: userRes.data.username,
                name: userRes.data.name || '',
                email: userRes.data.email || '',
                timezone: userRes.data.timezone || '',
                openBalance: parseFloat(userRes.data.openBalance || 0).toFixed(2),
            });
        } catch (error) {
            toast.error(error.response?.data?.message || 'not fetch');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleGeneralUpdate = async () => {
        if (!userInfo.email || !userInfo.name || !selectedTimezone || userInfo.openBalance === '') {
            toast.error("Please fill in all fields.");
            return;
        }

        setLoading(true);
        try {
            await axiosInstance.patch(API_ENDPOINTS.PROFILE, {
                name: userInfo.name,
                email: userInfo.email,
                timezone: selectedTimezone.value,
                openBalance: parseFloat(userInfo.openBalance),
            });
            toast.success("General settings updated successfully!");
            fetchUserInfo();
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update general settings.");
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordUpdate = async () => {
        if (!passwords.new || !passwords.confirm) {
            toast.error("Please fill all password fields.");
            return;
        }
        if (passwords.new !== passwords.confirm) {
            toast.error("New passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            await axiosInstance.patch(API_ENDPOINTS.PROFILE, {
                newPassword: passwords.new,
            });
            toast.success("Password updated successfully!");
            setPasswords({ new: "", confirm: "" });
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update password.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserInfo();
    }, [fetchUserInfo]);

    return (
        <div className="bg-black text-white px-6 py-8">
            {loading &&
                <ThreeDots
                    visible={true}
                    height="80"
                    width="80"
                    color="#1976d2"
                    radius="9"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClass="dots-loading additional-class"
                />
            }
            <h1 className="text-3xl font-bold mb-8">Settings</h1>

            <div className="flex mb-8 p-2 bg-[#231f1f]">
                <button
                    className={`flex-1 py-1 text-md font-medium ${tab === "general" ? "bg-primary text-white" : "text-gray-300"}`}
                    onClick={() => setTab("general")}
                >
                    General Settings
                </button>
                <button
                    className={`flex-1 py-1 text-md font-medium ${tab === "password" ? "bg-primary text-white" : "text-gray-300"}`}
                    onClick={() => setTab("password")}
                >
                    Change Password
                </button>
            </div>

            <div className="bg-[#111] border border-border rounded p-4 md:p-8">
                {tab === "general" && (
                    <>
                        <h2 className="text-lg font-bold mb-6">General Settings</h2>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">Username</label>
                            <input value={userInfo.username} disabled className="w-full bg-gray-800 border border-border rounded px-4 py-2 text-white" />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">Name</label>
                            <input
                                type="text"
                                value={userInfo.name}
                                onChange={e => setUserInfo({ ...userInfo, name: e.target.value })}
                                className="w-full bg-black border border-border rounded px-4 py-2 text-white"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">Email</label>
                            <input
                                type="email"
                                value={userInfo.email}
                                onChange={e => setUserInfo({ ...userInfo, email: e.target.value })}
                                className="w-full bg-black border border-border rounded px-4 py-2 text-white"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">Timezone</label>
                            <Select
                                value={selectedTimezone}
                                onChange={option => setSelectedTimezone(option)}
                                options={timezoneOptions}
                                styles={customStyles}
                                className="w-full"
                                placeholder="Select your timezone"
                                isSearchable
                            />
                        </div>
                        <div className="mb-8">
                            <label className="block mb-2 font-medium">Opening Balance</label>
                            <input
                                type="number"
                                step="0.01"
                                value={userInfo.openBalance}
                                onChange={e => setUserInfo({ ...userInfo, openBalance: e.target.value })}
                                className="w-full bg-black border border-border rounded px-4 py-2 text-white"
                            />
                        </div>
                        <button
                            className="bg-secondary hover:opacity-75 px-4 py-2 rounded text-white w-full"
                            onClick={handleGeneralUpdate}
                            disabled={loading}
                        >
                            Update Settings
                        </button>
                    </>
                )}

                {tab === "password" && (
                    <>
                        <h2 className="text-lg font-bold mb-6">Change Password</h2>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">New Password</label>
                            <input
                                type="password"
                                className="w-full bg-black border border-border rounded px-4 py-3 text-white"
                                placeholder="Enter your new password"
                                value={passwords.new}
                                onChange={e => setPasswords({ ...passwords, new: e.target.value })}
                            />
                        </div>
                        <div className="mb-8">
                            <label className="block mb-2 font-medium">Confirm New Password</label>
                            <input
                                type="password"
                                className="w-full bg-black border border-border rounded px-4 py-3 text-white"
                                placeholder="Confirm your new password"
                                value={passwords.confirm}
                                onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                            />
                        </div>
                        <button
                            className="bg-secondary hover:opacity-75 px-4 py-2 rounded text-white w-full"
                            onClick={handlePasswordUpdate}
                            disabled={loading}
                        >
                            Change Password
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
